import Loading from '@/common/atoms/Loading'
import Page404 from '@/common/pages/Page404'
import useOrgMember from '@/member/hooks/useOrgMember'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Container,
  useDisclosure,
} from '@chakra-ui/react'
import { useOrgContext } from '@/org/contexts/OrgContext'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { VisioIcon } from 'src/icons'
import { MeetingContext } from '../contexts/MeetingContext'
import useCreateMissingMeetingSteps from '../hooks/useCreateMissingMeetingSteps'
import MeetingEditModal from '../modals/MeetingEditModal'
import MeetingAlertForceEdit from './MeetingAlertForceEdit'
import MeetingAlertNotStarted from './MeetingAlertNotStarted'
import MeetingAttendeesList from './MeetingAttendeesList'
import MeetingFacilitationSheets from './MeetingFacilitationSheets'
import MeetingLogs from './MeetingLogs'
import MeetingStepContent from './MeetingStepContent'
import { taskLogTypes } from './MeetingStepContentTasks'
import MeetingStepLayout from './MeetingStepLayout'
import MeetingThreadsDragDropContext from './MeetingThreadsDragDropContext'

export default function MeetingContent() {
  const { t } = useTranslation()
  const { org } = useOrgContext()
  const isMember = useOrgMember()

  const {
    meeting,
    error,
    steps,
    circle,
    isParticipant,
    editable,
    canEdit,
    forceEdit,
    isEnded,
    isNotStarted,
    isStarted,
    videoConfUrl,
    handleGoToStep,
  } = useContext(MeetingContext)!

  // Create missing steps
  //
  // 🔑 Les deux gardes ci-dessous ne sont pas des précautions superflues : elles
  // viennent de la condition d'affichage de MeetingContentEmpty, l'écran qui
  // tenait le cas « aucune étape » avant son retrait (#97). Sans elles, tout
  // membre de l'organisation créerait les étapes en ouvrant la réunion, y
  // compris quelqu'un qui vient seulement lire.
  //
  // Ce composant est désormais le seul à créer les étapes d'une réunion qu'on
  // ouvre (l'autre chemin de création, useDuplicateMeetingSteps, vise une
  // réunion neuve qui n'est pas encore affichée). La course reste arbitrée par
  // le `on_conflict` de createMeetingStep et par la garde en vol du hook, qui
  // protège la passe d'un effet relancé par sa propre insertion.
  const createMissingMeetingSteps = useCreateMissingMeetingSteps()
  useEffect(() => {
    if (!meeting || !circle || !steps) return
    if (!isParticipant || meeting.archivedAt) return
    createMissingMeetingSteps()
  }, [meeting, circle, steps, isParticipant])

  // Meeting edition modal
  const editModal = useDisclosure()

  if (error || !meeting) {
    console.error(error)
    return <Page404 />
  }

  return (
    <Container maxW="3xl" py={10}>
      {isMember && !isEnded && !isParticipant && (
        <Alert status="info" mb={16}>
          <AlertIcon />
          <AlertDescription>{t('MeetingContent.notInvited')}</AlertDescription>
        </Alert>
      )}

      {meeting.archivedAt && (
        <Alert status="info" mb={16}>
          <AlertIcon />
          <AlertDescription>{t('MeetingContent.archived')}</AlertDescription>
        </Alert>
      )}

      {/* Les étapes sont en train de naître : sans ce témoin, la réunion
          s'afficherait vide et muette le temps des insertions, qui sont
          séquentielles (#97). MeetingContentEmpty le montrait avant son
          retrait, à cette place et à cette condition exacte. */}
      {isParticipant &&
        !meeting.archivedAt &&
        steps?.length === 0 &&
        meeting.stepsConfig.length !== 0 && <Loading active size="md" />}

      {isParticipant &&
        !meeting.archivedAt &&
        (isNotStarted ? (
          <MeetingAlertNotStarted mb={16} onEdit={editModal.onOpen} />
        ) : (
          isEnded && forceEdit && <MeetingAlertForceEdit mb={16} />
        ))}

      <Collapse
        in={!!videoConfUrl && isStarted && isParticipant}
        animateOpacity
      >
        <Box mb={16}>
          <a href={videoConfUrl} target="_blank" rel="noreferrer">
            <Button leftIcon={<VisioIcon variant="Bold" />} colorScheme="blue">
              {t('MeetingContent.videoConf')}
            </Button>
          </a>
        </Box>
      </Collapse>

      {/* The facilitation sheets of the steps this meeting holds (#73):
          under the start, above the attendance (Yohan). Not when the meeting
          is only read — ended, archived, or not editable by this person —,
          nor when the org turned the meeting aids off (#136, Yohan 27/09) */}
      {editable && org?.meetingAidsEnabled !== false && (
        <MeetingFacilitationSheets
          stepTypes={meeting.stepsConfig.map((stepConfig) => stepConfig.type)}
        />
      )}

      <MeetingAttendeesList mb={16} />

      <MeetingThreadsDragDropContext disabled={!editable}>
        {meeting.stepsConfig.map((stepConfig, index) => {
          const step = steps?.find((s) => s.stepConfigId === stepConfig.id)
          if (!step) return null
          const current = meeting.currentStepId === step.id

          return (
            <MeetingStepLayout
              key={step.id}
              index={index}
              stepId={step.id}
              title={stepConfig.title}
              type={stepConfig.type}
              showAids={editable}
              current={current}
              onStepClick={
                isStarted && canEdit ? () => handleGoToStep(step.id) : undefined
              }
            >
              <MeetingStepContent step={step} />
            </MeetingStepLayout>
          )
        })}
      </MeetingThreadsDragDropContext>

      {!isNotStarted && (
        <MeetingLogs
          meetingId={meeting.id}
          title={t('MeetingContent.logs')}
          excludeTypes={taskLogTypes}
          hideEmpty
          mt={10}
        />
      )}

      <Box h="100px" />

      {editModal.isOpen && (
        <MeetingEditModal
          meeting={meeting}
          isOpen
          onClose={editModal.onClose}
        />
      )}
    </Container>
  )
}
